import { useState, useEffect } from "react";
import axios from "axios";
import { Package, Plus, Edit2, Trash2, Check, Clock, ShoppingCart, AlertTriangle, X, Truck, RefreshCw, Search, PackageCheck, PackageMinus, Link, Info } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AccessoriesPage() {
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWO, setSelectedWO] = useState(null);
  const [accessories, setAccessories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pendingAll, setPendingAll] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingAcc, setEditingAcc] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    quantity: "",
    unit: "pcs",
    status: "pending",
    po_number: "",
    notes: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  
  // Receipt Modal State
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptAcc, setReceiptAcc] = useState(null);
  const [receiptAccIndex, setReceiptAccIndex] = useState(null);
  const [receiptType, setReceiptType] = useState("partial");
  const [receiptQty, setReceiptQty] = useState("");
  const [receiptNotes, setReceiptNotes] = useState("");
  const [receiptLoading, setReceiptLoading] = useState(false);
  
  // Link PO Modal State
  const [showLinkPOModal, setShowLinkPOModal] = useState(false);
  const [linkPOAcc, setLinkPOAcc] = useState(null);
  const [linkPONumber, setLinkPONumber] = useState("");
  const [availablePOs, setAvailablePOs] = useState([]);

  const accessoryPresets = [
    "Labels (Option 1)", 
    "Labels (Option 2)", 
    "Tags", 
    "Cartons", 
    "Polybags", 
    "Carton Marking", 
    "Warning Stickers", 
    "Barcode Stickers", 
    "Polybag Sticker",
    "Stitching Yarn"
  ];

  useEffect(() => {
    fetchWorkOrders();
    fetchPendingAccessories();
  }, []);

  const fetchWorkOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/work-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkOrders(response.data);
    } catch (err) {
      console.error("Failed to fetch work orders:", err);
    }
  };

  const fetchPendingAccessories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/accessories/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingAll(response.data.pending_accessories || []);
    } catch (err) {
      console.error("Failed to fetch pending accessories:", err);
    }
  };

  const handleWOSelect = async (woId) => {
    const wo = workOrders.find(w => w.id === woId);
    setSelectedWO(wo);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/work-orders/${woId}/accessories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns array directly, not wrapped in object
      const accessoriesData = Array.isArray(response.data) ? response.data : (response.data.accessories || []);
      console.log(`Loaded ${accessoriesData.length} accessories for WO ${woId}`);
      setAccessories(accessoriesData);
      
      // Fetch WO details for summary info
      const woResponse = await axios.get(`${API}/work-orders/${woId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch linked POs (POs linked to this work order)
      let linkedPOs = [];
      try {
        const allPOs = await axios.get(`${API}/purchase-orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Filter POs that are linked to this work order (support both single and multi-WO)
        linkedPOs = (allPOs.data || []).filter(po => 
          po.linked_wo_id === woId || 
          po.linked_wo_id === woResponse.data.wo_number ||
          (po.linked_wo_ids && (po.linked_wo_ids.includes(woId) || po.linked_wo_ids.includes(woResponse.data.wo_number))) ||
          po.customer_po_number === woResponse.data.customer_po_number
        );
      } catch (poErr) {
        console.log("Could not fetch linked POs:", poErr.message);
      }
      
      setSummary({
        total: accessoriesData.length,
        pending: accessoriesData.filter(a => a.status === 'pending').length,
        ordered: accessoriesData.filter(a => a.status === 'ordered').length,
        received: accessoriesData.filter(a => a.status === 'received').length,
        customer_po_number: woResponse.data.customer_po_number || '',
        linked_pos: linkedPOs
      });
    } catch (err) {
      console.error("Failed to fetch accessories:", err);
      setError(`Failed to load accessories: ${err.message}`);
      setAccessories([]);
      setSummary(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        quantity: parseInt(formData.quantity)
      };

      if (editingAcc) {
        // Use id if available, otherwise use name for legacy accessories
        const accIdentifier = editingAcc.id || encodeURIComponent(editingAcc.name);
        await axios.put(`${API}/work-orders/${selectedWO.id}/accessories/${accIdentifier}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess("Accessory updated successfully!");
      } else {
        await axios.post(`${API}/work-orders/${selectedWO.id}/accessories`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess("Accessory added successfully!");
      }

      handleWOSelect(selectedWO.id);
      fetchPendingAccessories();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save accessory");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (acc) => {
    setEditingAcc(acc);
    setFormData({
      name: acc.name,
      quantity: acc.quantity.toString(),
      unit: acc.unit,
      status: acc.status,
      po_number: acc.po_number || "",
      notes: acc.notes || ""
    });
    setShowForm(true);
  };

  const handleDelete = async (accId) => {
    if (!window.confirm("Delete this accessory?")) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/work-orders/${selectedWO.id}/accessories/${accId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      handleWOSelect(selectedWO.id);
      fetchPendingAccessories();
      setSuccess("Accessory deleted!");
    } catch (err) {
      setError("Failed to delete accessory");
    }
  };

  const resetForm = () => {
    setFormData({ name: "", quantity: "", unit: "pcs", status: "pending", po_number: "", notes: "" });
    setEditingAcc(null);
    setShowForm(false);
  };

  const handleMarkShipped = async () => {
    if (!window.confirm("Mark this Work Order as shipped?")) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/work-orders/${selectedWO.id}/mark-shipped`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Work Order marked as shipped!");
      fetchWorkOrders();
      setSelectedWO(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to mark as shipped");
    }
  };

  const getStatusBadge = (status, autoStatus) => {
    const autoTag = autoStatus ? <span className="ml-1 text-[10px] opacity-70">(auto)</span> : null;
    switch (status) {
      case 'received':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300"><Check className="w-3 h-3 mr-1" /> Received{autoTag}</Badge>;
      case 'partial_received':
        return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300"><PackageMinus className="w-3 h-3 mr-1" /> Partial{autoTag}</Badge>;
      case 'ordered':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300"><ShoppingCart className="w-3 h-3 mr-1" /> Ordered{autoTag}</Badge>;
      default:
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
    }
  };

  // Open receipt modal
  const openReceiptModal = (acc, index) => {
    setReceiptAcc(acc);
    setReceiptAccIndex(index);
    setReceiptType("partial");
    setReceiptQty("");
    setReceiptNotes("");
    setShowReceiptModal(true);
  };

  // Handle receipt submission
  const handleReceiptSubmit = async () => {
    if (!receiptAcc || receiptAccIndex === null) return;
    
    const orderedQty = receiptAcc.quantity;
    const currentReceived = receiptAcc.received_qty || 0;
    const balance = orderedQty - currentReceived;
    
    // Validation - quantity is required for both types now
    if (!receiptQty || parseInt(receiptQty) <= 0) {
      setError("Please enter a valid received quantity");
      return;
    }
    
    setReceiptLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        wo_id: selectedWO.id,
        accessory_index: receiptAccIndex,
        receipt_type: receiptType,
        received_qty: parseInt(receiptQty),
        receipt_date: new Date().toISOString().split('T')[0],
        notes: receiptNotes
      };
      
      await axios.post(`${API}/work-orders/${selectedWO.id}/accessories/${receiptAccIndex}/receipt`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess("Receipt recorded successfully!");
      setShowReceiptModal(false);
      handleWOSelect(selectedWO.id);
      fetchPendingAccessories();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to record receipt");
    } finally {
      setReceiptLoading(false);
    }
  };

  // Open Link PO modal
  const openLinkPOModal = async (acc) => {
    setLinkPOAcc(acc);
    setLinkPONumber(acc.po_number || "");
    setShowLinkPOModal(true);
    
    // Fetch available POs (linked via customer_po_number or all POs)
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/purchase-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to recent/relevant POs
      const pos = res.data.filter(po => 
        po.customer_po_number === selectedWO?.customer_po_number ||
        po.status === 'sent' || po.status === 'partial_received'
      ).slice(0, 50);
      setAvailablePOs(pos);
    } catch (err) {
      console.error("Failed to fetch POs", err);
      setAvailablePOs([]);
    }
  };
  
  // Handle Sync POs - auto-link accessories with existing linked POs
  const handleSyncPOs = async () => {
    if (!selectedWO) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/work-orders/${selectedWO.id}/sync-accessory-pos`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.linked > 0) {
        setSuccess(`✅ Synced ${res.data.linked} accessories with POs`);
        handleWOSelect(selectedWO.id); // Refresh data
      } else {
        setError("No matching POs found to sync. Try linking manually.");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to sync POs");
    }
  };
  
  // Handle Restore Accessories from linked POs (data recovery)
  const handleRestoreFromPOs = async () => {
    if (!window.confirm('This will recreate accessories from linked Purchase Orders. Continue?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/work-orders/${selectedWO.id}/restore-accessories-from-pos`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.restored > 0) {
        setSuccess(`✅ Restored ${res.data.restored} accessories from ${res.data.linked_pos_count} POs`);
        // Pass the ID, not the whole object
        handleWOSelect(selectedWO.id);
      } else {
        setError("No accessories could be restored. Check if linked POs exist.");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to restore accessories");
    }
  };
  
  // Handle Clear PO from accessory
  const handleClearPO = async (acc) => {
    if (!window.confirm(`Clear PO link from "${acc.name}"? This will reset the status to Pending.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/work-orders/${selectedWO.id}/accessories/${acc.id}/clear-po`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`PO link cleared from "${acc.name}"`);
      handleWOSelect(selectedWO.id);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to clear PO link");
    }
  };
  
  // Handle Link PO submission
  const handleLinkPO = async () => {
    if (!linkPOAcc || !linkPONumber) {
      setError("Please select a PO number");
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/work-orders/${selectedWO.id}/accessories/${linkPOAcc.id}/link-po`,
        { po_number: linkPONumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`Accessory linked to ${linkPONumber}`);
      setShowLinkPOModal(false);
      handleWOSelect(selectedWO.id);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to link PO");
    }
  };

  const filteredWorkOrders = workOrders.filter(wo => 
    wo.wo_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wo.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wo.customer_po_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Package className="w-6 h-6" />
            Accessories Management
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Track and manage accessories for Work Orders</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchWorkOrders(); fetchPendingAccessories(); }}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Pending Accessories Alert - BOLD BLACK */}
      {pendingAll.length > 0 && (
        <Card className="bg-black border-black text-white">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-white text-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              {pendingAll.length} PENDING ACCESSORIES NEED ORDERING
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {pendingAll.slice(0, 8).map((item, idx) => (
                <Badge 
                  key={idx} 
                  variant="outline"
                  className={`${
                    item.production_started 
                      ? 'bg-red-600 text-white border-red-400' 
                      : 'bg-yellow-500 text-black border-yellow-400'
                  }`}
                >
                  {item.production_started && '🔴 '}{item.wo_number}: {item.accessory.name} ({item.accessory.quantity} {item.accessory.unit})
                </Badge>
              ))}
              {pendingAll.length > 8 && (
                <Badge variant="outline" className="bg-gray-700 text-white">+{pendingAll.length - 8} more</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Work Orders List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Work Orders</CardTitle>
            <CardDescription>Select to manage accessories</CardDescription>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search WO..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0 max-h-[500px] overflow-y-auto">
            {filteredWorkOrders.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground text-sm">No work orders found</div>
            ) : (
              <div className="divide-y">
                {filteredWorkOrders.map((wo) => (
                  <div
                    key={wo.id}
                    onClick={() => handleWOSelect(wo.id)}
                    data-testid={`wo-select-${wo.id}`}
                    className={`p-3 cursor-pointer hover:bg-muted/50 transition-colors ${
                      selectedWO?.id === wo.id ? 'bg-primary/10 border-l-4 border-l-primary' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-sm">{wo.wo_number}</div>
                        <div className="text-xs text-muted-foreground">{wo.customer_name}</div>
                        {wo.customer_po_number && (
                          <div className="text-xs text-blue-600">PO: {wo.customer_po_number}</div>
                        )}
                      </div>
                      {wo.accessories?.length > 0 && (
                        <Badge variant="secondary" className="text-xs">{wo.accessories.length}</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Accessories Management */}
        <Card className="lg:col-span-2">
          {!selectedWO ? (
            <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Package className="w-12 h-12 mb-4 opacity-30" />
              <p>👈 Select a Work Order to manage accessories</p>
            </CardContent>
          ) : (
            <>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{selectedWO.wo_number}</CardTitle>
                    <CardDescription>{selectedWO.customer_name}</CardDescription>
                    {selectedWO.customer_po_number && (
                      <p className="text-sm text-blue-600 font-medium mt-1">Customer PO: {selectedWO.customer_po_number}</p>
                    )}
                    {/* Total Order & Plan Pcs */}
                    <div className="flex flex-wrap gap-3 mt-2">
                      <div className="bg-slate-100 px-2 py-1 rounded text-xs">
                        <span className="text-muted-foreground">Order Pcs:</span>
                        <span className="font-bold ml-1">{selectedWO.total_qty_pcs?.toLocaleString() || 0}</span>
                      </div>
                      <div className="bg-blue-100 px-2 py-1 rounded text-xs">
                        <span className="text-blue-700">Plan Pcs:</span>
                        <span className="font-bold ml-1 text-blue-700">
                          {(selectedWO.order_items?.reduce((sum, i) => sum + (parseInt(i.plan_pcs) || 0), 0) || selectedWO.total_qty_pcs || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-accessory-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                    {/* Restore from POs button - ALWAYS shows when linked POs exist */}
                    {summary?.linked_pos?.length > 0 && (
                      <Button 
                        size="sm" 
                        variant="default" 
                        className="bg-orange-600 hover:bg-orange-700"
                        onClick={handleRestoreFromPOs}
                        data-testid="restore-from-pos-btn"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" /> Restore from POs
                      </Button>
                    )}
                    {/* Sync POs button - links accessories with existing POs */}
                    {summary?.linked_pos?.length > 0 && accessories.length > 0 && accessories.some(a => !a.po_number) && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-blue-600 border-blue-300 hover:bg-blue-50"
                        onClick={handleSyncPOs}
                        data-testid="sync-pos-btn"
                      >
                        <Link className="w-4 h-4 mr-1" /> Sync POs
                      </Button>
                    )}
                    {selectedWO.current_stage !== 'shipped' && summary?.all_received && (
                      <Button size="sm" variant="default" className="bg-green-600 hover:bg-green-700" onClick={handleMarkShipped} data-testid="mark-shipped-btn">
                        <Truck className="w-4 h-4 mr-1" /> Ship
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Size-wise Plan Pcs Breakdown */}
                {selectedWO.order_items?.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs font-bold text-blue-800 mb-2">📐 Size-wise Plan Pcs</p>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-blue-100">
                            <TableHead className="text-xs">Size</TableHead>
                            <TableHead className="text-xs">Quality</TableHead>
                            <TableHead className="text-xs text-right">Order Pcs</TableHead>
                            <TableHead className="text-xs text-right">Plan Pcs</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedWO.order_items.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium text-xs">{item.size || item.size_cm}</TableCell>
                              <TableCell className="text-xs">{item.quality || item.design_name}</TableCell>
                              <TableCell className="text-right text-xs">{(item.order_pcs || 0).toLocaleString()}</TableCell>
                              <TableCell className="text-right text-xs font-semibold text-blue-600">{(item.plan_pcs || item.order_pcs || 0).toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-blue-100 font-bold">
                            <TableCell colSpan={2}>Total</TableCell>
                            <TableCell className="text-right">{selectedWO.order_items.reduce((sum, i) => sum + (parseInt(i.order_pcs) || 0), 0).toLocaleString()}</TableCell>
                            <TableCell className="text-right text-blue-600">{selectedWO.order_items.reduce((sum, i) => sum + (parseInt(i.plan_pcs) || parseInt(i.order_pcs) || 0), 0).toLocaleString()}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Linked POs */}
                {summary?.linked_pos?.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs font-bold text-blue-800 mb-2">📦 Linked Purchase Orders (via Customer PO #{summary.customer_po_number})</p>
                    <div className="flex flex-wrap gap-2">
                      {summary.linked_pos.map((po, idx) => (
                        <Badge key={idx} variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                          {po.po_number} ({po.vendor_name}) - {po.status}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-[10px] text-blue-600 mt-2">Status auto-updates based on PO receipts</p>
                  </div>
                )}

                {/* Summary Cards */}
                {summary && (
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-muted/50 p-3 rounded-lg text-center">
                      <div className="text-2xl font-bold">{summary.total}</div>
                      <div className="text-xs text-muted-foreground">Total</div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-center">
                      <div className="text-2xl font-bold text-yellow-600">{summary.pending}</div>
                      <div className="text-xs text-muted-foreground">Pending</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-center">
                      <div className="text-2xl font-bold text-blue-600">{summary.ordered}</div>
                      <div className="text-xs text-muted-foreground">Ordered</div>
                    </div>
                    <div className="bg-green-50 border border-green-200 p-3 rounded-lg text-center">
                      <div className="text-2xl font-bold text-green-600">{summary.received}</div>
                      <div className="text-xs text-muted-foreground">Received</div>
                    </div>
                  </div>
                )}

                {/* Success/Error Messages */}
                {success && <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded-lg text-sm">{success}</div>}
                {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">{error}</div>}

                {/* Accessories Table */}
                {accessories.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p>No accessories added yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Accessory</TableHead>
                        <TableHead className="text-right">Ordered Qty</TableHead>
                        <TableHead className="text-right">Received</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead>PO #</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accessories.map((acc, accIndex) => {
                        const orderedQty = acc.quantity || 0;
                        const receivedQty = acc.received_qty || 0;
                        const balance = orderedQty - receivedQty;
                        const receiptProgress = orderedQty > 0 ? (receivedQty / orderedQty) * 100 : 0;
                        
                        return (
                          <TableRow key={acc.id}>
                            <TableCell>
                              <div className="font-medium">{acc.name}</div>
                              {acc.notes && <div className="text-xs text-muted-foreground">{acc.notes}</div>}
                              {acc.unit && <div className="text-xs text-muted-foreground">Unit: {acc.unit}</div>}
                            </TableCell>
                            <TableCell className="text-right font-semibold">{orderedQty}</TableCell>
                            <TableCell className="text-right">
                              <span className={receivedQty > orderedQty ? "text-red-600 font-bold" : receivedQty > 0 ? "text-green-600 font-medium" : "text-muted-foreground"}>
                                {receivedQty}
                              </span>
                              {receivedQty > orderedQty && (
                                <div className="text-[10px] text-red-500 font-medium">+{receivedQty - orderedQty} extra</div>
                              )}
                              {orderedQty > 0 && (
                                <div className="mt-1">
                                  <Progress value={Math.min(receiptProgress, 100)} className={`h-1.5 ${receiptProgress > 100 ? '[&>div]:bg-red-500' : ''}`} />
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {balance > 0 ? (
                                <div>
                                  <span className="text-orange-600 font-medium">{balance}</span>
                                  <div className="text-[10px] text-orange-500">pending</div>
                                </div>
                              ) : balance < 0 ? (
                                <div>
                                  <span className="text-red-600 font-bold">{Math.abs(balance)}</span>
                                  <div className="text-[10px] text-red-500 font-medium">extra received</div>
                                </div>
                              ) : (
                                <span className="text-green-600">0</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">{getStatusBadge(acc.status, acc.auto_status)}</TableCell>
                            <TableCell className="text-sm">
                              {acc.po_number ? (
                                <div className="flex items-center gap-1">
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                                    {acc.po_number}
                                  </Badge>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0"
                                    onClick={() => handleClearPO(acc)}
                                    title="Clear PO link"
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7 px-2"
                                  onClick={() => openLinkPOModal(acc)}
                                  data-testid={`link-po-btn-${acc.id}`}
                                >
                                  <Link className="w-3 h-3 mr-1" />
                                  Link PO
                                </Button>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex justify-center gap-1">
                                {/* Receive Button - Show for pending, ordered, partial_received, or received (for extra qty) */}
                                {(acc.status === 'pending' || acc.status === 'ordered' || acc.status === 'partial_received' || acc.status === 'received') && (
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                    onClick={() => openReceiptModal(acc, accIndex)}
                                    data-testid={`receive-btn-${acc.id}`}
                                  >
                                    <PackageCheck className="w-4 h-4 mr-1" />
                                    Receive
                                  </Button>
                                )}
                                <Button variant="outline" size="sm" className="h-10 w-10 min-w-[44px] min-h-[44px]" onClick={() => handleEdit(acc)}>
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => handleDelete(acc.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  </div>
                )}
              </CardContent>
            </>
          )}
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingAcc ? 'Edit Accessory' : 'Add Accessory'}</DialogTitle>
            <DialogDescription>
              {editingAcc ? 'Update accessory details' : 'Add a new accessory to this Work Order'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Accessory Type *</Label>
              <Select value={formData.name} onValueChange={(value) => setFormData({ ...formData, name: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select accessory type" />
                </SelectTrigger>
                <SelectContent>
                  {accessoryPresets.map((preset) => (
                    <SelectItem key={preset} value={preset}>{preset}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  required
                  min="1"
                />
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pcs">Pieces</SelectItem>
                    <SelectItem value="boxes">Boxes</SelectItem>
                    <SelectItem value="rolls">Rolls</SelectItem>
                    <SelectItem value="sheets">Sheets</SelectItem>
                    <SelectItem value="kg">KG</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="ordered">Ordered</SelectItem>
                    <SelectItem value="partial_received">Partial Received</SelectItem>
                    <SelectItem value="received">Received</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>PO Number</Label>
                <Input
                  value={formData.po_number}
                  onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Special instructions..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : (editingAcc ? 'Update' : 'Add Accessory')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageCheck className="w-5 h-5 text-green-600" />
              Record Receipt
            </DialogTitle>
            <DialogDescription>
              Record received quantity for {receiptAcc?.name}
            </DialogDescription>
          </DialogHeader>
          
          {receiptAcc && (
            <div className="space-y-4">
              {/* Current Status */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Ordered Quantity:</span>
                  <span className="font-semibold">{receiptAcc.quantity} {receiptAcc.unit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Already Received:</span>
                  <span className="font-semibold text-green-600">{receiptAcc.received_qty || 0}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-sm font-medium">Balance Pending:</span>
                  <span className="font-bold text-orange-600">{receiptAcc.quantity - (receiptAcc.received_qty || 0)}</span>
                </div>
              </div>

              {/* Balance Warning Message */}
              {(receiptAcc.quantity - (receiptAcc.received_qty || 0)) > 0 ? (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-700">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Balance pending: <strong>{receiptAcc.quantity - (receiptAcc.received_qty || 0)} {receiptAcc.unit}</strong> yet to be received
                </div>
              ) : (receiptAcc.received_qty || 0) >= receiptAcc.quantity ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                  <Info className="w-4 h-4 inline mr-2" />
                  All ordered qty already received. Any new receipt will be marked as extra quantity.
                </div>
              ) : null}

              {/* Receipt Type */}
              <div className="space-y-2">
                <Label>Receipt Type</Label>
                <Select value={receiptType} onValueChange={(v) => {
                  setReceiptType(v);
                  if (v === "full") {
                    const bal = receiptAcc.quantity - (receiptAcc.received_qty || 0);
                    setReceiptQty(String(Math.max(bal, 0)));
                  } else {
                    setReceiptQty("");
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="partial">Received Partially</SelectItem>
                    <SelectItem value="full">Received in Full</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Quantity Input - Show for both types */}
              {(() => {
                const orderedQty = receiptAcc.quantity;
                const currentReceived = receiptAcc.received_qty || 0;
                const balance = orderedQty - currentReceived;
                const enteredQty = parseInt(receiptQty) || 0;
                const wouldExceed = (currentReceived + enteredQty) > orderedQty;
                return (
                  <div className="space-y-2">
                    <Label>Received Quantity (pcs) *</Label>
                    <Input
                      type="number"
                      value={receiptQty}
                      onChange={(e) => setReceiptQty(e.target.value)}
                      placeholder="Enter quantity received"
                      min="1"
                      className={wouldExceed && enteredQty > 0 ? "border-red-500 text-red-600 font-semibold ring-1 ring-red-300" : ""}
                      data-testid="receipt-qty-input"
                    />
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">
                        Balance: {balance > 0 ? balance : 0} {receiptAcc.unit}
                      </span>
                      {wouldExceed && enteredQty > 0 && (
                        <span className="text-red-600 font-semibold" data-testid="excess-qty-warning">
                          Extra: +{(currentReceived + enteredQty) - orderedQty} over ordered qty
                        </span>
                      )}
                    </div>
                    {wouldExceed && enteredQty > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-sm text-red-700 flex items-center gap-2" data-testid="excess-qty-alert">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        <span>Receiving <strong>{enteredQty}</strong> will exceed ordered qty of <strong>{orderedQty}</strong> (total received: <strong className="text-red-800">{currentReceived + enteredQty}</strong>)</span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Input
                  value={receiptNotes}
                  onChange={(e) => setReceiptNotes(e.target.value)}
                  placeholder="Any notes about this receipt..."
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceiptModal(false)}>Cancel</Button>
            <Button 
              onClick={handleReceiptSubmit} 
              disabled={receiptLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {receiptLoading ? 'Saving...' : 'Record Receipt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link PO Dialog */}
      <Dialog open={showLinkPOModal} onOpenChange={setShowLinkPOModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link className="w-5 h-5 text-blue-600" />
              Link Purchase Order
            </DialogTitle>
            <DialogDescription>
              Link a Purchase Order to "{linkPOAcc?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select PO Number *</Label>
              <Select value={linkPONumber} onValueChange={setLinkPONumber}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a Purchase Order" />
                </SelectTrigger>
                <SelectContent>
                  {availablePOs.map((po) => (
                    <SelectItem key={po.id} value={po.po_number}>
                      {po.po_number} - {po.vendor_name} ({po.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {availablePOs.length === 0 && (
              <div className="text-sm text-muted-foreground">
                No Purchase Orders found. Create a PO first or enter manually.
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Or Enter PO Number Manually</Label>
              <Input
                value={linkPONumber}
                onChange={(e) => setLinkPONumber(e.target.value)}
                placeholder="e.g., PO-0001"
              />
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkPOModal(false)}>Cancel</Button>
            <Button 
              onClick={handleLinkPO} 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!linkPONumber}
            >
              Link PO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
